import request from '@/plugins/request';
import { IBoolResult, IErrorResult, IExecuteResult, IStringResult } from '@/types/dto';
import { IHost, IHostBackup, IHostDomain } from '@/types/models';
import utils from '@/utils';
import { AxiosError, AxiosResponse } from 'axios';

const $url = '/my/hosts';
const $urlBackups = '/my/hosts/backups';
const $urlBackupsRestore = '/my/hosts/backups/actions/restore';
const $urlCredentials = '/my/hosts/credentials';
const $urlInstall = '/my/hosts/install';
const $urlPay = '/my/hosts/actions/pay';
const $urlQuery = '/my/hosts/actions/query';
const $urlName = '/my/hosts/actions/name';
const $urlUpgrade = '/my/hosts/actions/upgrade';
const $urlGetDomains = '/my/hosts/actions/getDomains';
const $urlSetDomain = '/my/hosts/actions/setDomain';
const $urlSetDomainQuery = '/my/hosts/actions/setDomainQuery';
const $urlStart = '/my/hosts/actions/start';
const $urlStop = '/my/hosts/actions/stop';
const $urlReload = '/my/hosts/actions/reload';

interface IHostRequest {
  hostId: number;
}

interface IGetResult {
  hosts: IHost[];
  priceStarter: number;
  priceStandard: number;
  priceProfessional: number;
  discount1Year: number;
  discount2Year: number;
  discount3Year: number;
  discount4Year: number;
  discount5Year: number;
}

interface ICredentialsGetResult {
  isHongKong: boolean;
  securityKey: string;
  ftpPassword: string;
  databaseHost: string;
  databaseUser: string;
  databasePassword: string;
  databaseName: string;
}

interface IGetInstallResult {
  securityKey: string;
  themeName: string;
  themeDownloadUrl: string;
  guid: string;
}

interface IPayRequest {
  resourceType: string;
  hostId: number;
  hostType: string;
  year: number;
  channel: string;
  amount: number;
}

interface IPayResult {
  orderNo: string;
  qrCodeUrl: string;
}

interface IQueryRequest {
  orderNo: string;
}

interface IQueryResult {
  isPurchased: boolean;
  message: string;
  url: string;
}

export interface INameRequest {
  id: number;
  nickName: string;
}

interface IBackupsResult {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  backups: IHostBackup[];
}

interface IBackupRequest {
  hostId: number;
  backupId: number;
}

interface IUpgradeRequest {
  hostId: number;
  hostType: string;
}

interface IUpgradeResult {
  amount: number;
  extendDate: string;
}

interface IInstallSubmitRequest {
  hostId: number;
  userName: string;
  password: string;
}

interface IGetDomainsResult {
  hostName: string;
  nodeName: string;
  isHongKong: boolean;
  uniqueName: string;
  nickName: string;
  securityKey: string;
  domains: IHostDomain[];
}

interface ISetDomainRequest {
  hostId: number;
  siteIds: number[];
  siteId: number;
  prefix: string;
  domain: string;
  https: boolean;
  background: boolean;
}

export default {
  get(success: (res: IGetResult) => void) {
    request
      .get($url)
      .then((response: AxiosResponse<IGetResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  credentialsGet(hostId: number, success: (res: ICredentialsGetResult) => void) {
    utils.loading(true);
    request
      .get($urlCredentials, {
        params: { hostId },
      })
      .then((response: AxiosResponse<ICredentialsGetResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  installGet(hostId: number, success: (res: IGetInstallResult) => void) {
    utils.loading(true);
    request
      .get($urlInstall, {
        params: { hostId },
      })
      .then((response: AxiosResponse<IGetInstallResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  installSubmit(req: IInstallSubmitRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlInstall, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  pay(req: IPayRequest, success: (res: IPayResult) => void, failure: (error: AxiosError<IErrorResult> | string) => void) {
    utils.loading(true);
    request
      .post($urlPay, req)
      .then((response: AxiosResponse<IPayResult>) => {
        success(response.data);
        utils.loading(false);
      })
      .catch(error => {
        failure(error);
      });
  },

  query(req: IQueryRequest, success: (res: IQueryResult) => void) {
    request
      .post($urlQuery, req)
      .then((response: AxiosResponse<IQueryResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  name(req: INameRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlName, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsGet(hostId: number, success: (res: IBackupsResult) => void) {
    utils.loading(true);
    request
      .get($urlBackups, {
        params: { hostId },
      })
      .then((response: AxiosResponse<IBackupsResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsSubmit(hostId: number, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlBackups, {
        hostId,
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsDelete(req: IBackupRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .delete($urlBackups, {
        data: req,
      })
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  backupsRestore(req: IBackupRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlBackupsRestore, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  upgrade(req: IUpgradeRequest, success: (res: IUpgradeResult) => void) {
    utils.loading(true);
    request
      .post($urlUpgrade, req)
      .then((response: AxiosResponse<IUpgradeResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  getDomains(hostId: number, success: (res: IGetDomainsResult) => void) {
    utils.loading(true);
    request
      .post($urlGetDomains, {
        hostId,
      })
      .then((response: AxiosResponse<IGetDomainsResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
        utils.loading(false);
      });
  },

  setDomain(req: ISetDomainRequest, success: (res: IStringResult) => void) {
    request
      .post($urlSetDomain, req)
      .then((response: AxiosResponse<IStringResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  setDomainQuery(guid: string, success: (res: IExecuteResult) => void) {
    request
      .post($urlSetDomainQuery, {
        guid,
      })
      .then((response: AxiosResponse<IExecuteResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      });
  },

  start(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlStart, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  stop(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlStop, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },

  reload(req: IHostRequest, success: (res: IBoolResult) => void) {
    utils.loading(true);
    request
      .post($urlReload, req)
      .then((response: AxiosResponse<IBoolResult>) => {
        success(response.data);
      })
      .catch(error => {
        utils.error(error);
      })
      .then(() => {
        utils.loading(false);
      });
  },
};
